import React, { useCallback, useEffect, useState } from 'react'
import { ArrowLeft, Image as ImageIcon, Megaphone, MessageSquare, Trophy, Users, Video } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useAdminAuth } from '../../contexts/AdminAuthContext'
import { db } from '../../lib/db'
import {
  CommunityMemberRow,
  CommunityPostRow,
  PostArea,
  acceptCommunityPromise,
  createCommunityPost,
  fetchCommunityPosts,
  fetchMyCommunityMember,
  uploadCommunityMedia,
  upsertCommunityProfile,
} from '../../lib/communityData'
import CommunityPostCard from '../../components/community/CommunityPostCard'
import CommunityMembersPanel from '../../components/community/CommunityMembersPanel'
import GlassCard from '../../components/ui/GlassCard'
import GradientButton from '../../components/ui/GradientButton'
import PanelShell from '../../components/ui/PanelShell'

// Approved wording — Cassie's final Community Promise text.
const COMMUNITY_PROMISE_TEXT = `Our Community Promise

Welcome to the Fit by Cassie Community Center.

This is a space for connection, encouragement, honest conversations, and celebrating progress—at every stage of your journey.

By participating, I agree to:

- Lead with kindness. Treat other members with respect, compassion, and encouragement.
- Respect privacy. What someone shares here stays here. Do not screenshot, repost, or share another member's stories, photos, or personal information without their permission.
- Celebrate every kind of progress. Wins come in all shapes and sizes. There is no comparison, competition, or pressure to look or perform a certain way.
- Keep advice supportive. Share your own experiences without judging others or presenting personal opinions as medical advice.
- Honor boundaries. Do not pressure members to connect privately, share contact information, or disclose anything they aren't comfortable sharing.
- Keep business separate. This isn't a space to recruit other members for your own business, services, or clients — introductions are for encouragement and support, not solicitation.
- Help keep this space safe. Report concerning content and respect moderation decisions.

Cassie may remove content or suspend participation when necessary to protect the community.

I agree to the Community Promise and will do my part to make this a welcoming, respectful space.`

const AREAS: { key: PostArea; label: string; icon: typeof Megaphone }[] = [
  { key: 'announcement', label: 'Announcements', icon: Megaphone },
  { key: 'conversation', label: 'Conversations', icon: MessageSquare },
  { key: 'win', label: 'Wins', icon: Trophy },
]

type Tab = PostArea | 'members'

const CommunityCenter: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { isAdmin } = useAdminAuth()
  const { user: instantUser } = db.useAuth()
  const token = (instantUser as any)?.refresh_token || ''

  const [checkingGate, setCheckingGate] = useState(true)
  const [member, setMember] = useState<CommunityMemberRow | null>(null)
  const [gateName, setGateName] = useState('')
  const [gateIntro, setGateIntro] = useState('')
  const [gateAgreed, setGateAgreed] = useState(false)
  const [gateBusy, setGateBusy] = useState(false)
  const [gateError, setGateError] = useState('')

  const [tab, setTab] = useState<Tab>('conversation')
  const [posts, setPosts] = useState<CommunityPostRow[]>([])
  const [loadingPosts, setLoadingPosts] = useState(true)

  const [draft, setDraft] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [posting, setPosting] = useState(false)
  const [postError, setPostError] = useState('')

  const loadMember = useCallback(async () => {
    if (!user?.id) return
    setCheckingGate(true)
    try {
      const row = await fetchMyCommunityMember(user.id)
      setMember(row)
      if (row) {
        setGateName(row.displayName)
        setGateIntro(row.shortIntro)
      } else {
        setGateName(user.user_metadata?.full_name || '')
      }
    } catch (err) {
      console.error('Failed to load community profile:', err)
    } finally {
      setCheckingGate(false)
    }
  }, [user])

  useEffect(() => {
    loadMember()
  }, [loadMember])

  const loadPosts = useCallback(async (area: PostArea) => {
    setLoadingPosts(true)
    try {
      setPosts(await fetchCommunityPosts(area))
    } catch (err) {
      console.error('Failed to load community posts:', err)
    } finally {
      setLoadingPosts(false)
    }
  }, [])

  useEffect(() => {
    if (tab !== 'members' && member?.guidelinesAcknowledgedAt) {
      loadPosts(tab)
    }
  }, [tab, member, loadPosts])

  const handleAcceptPromise = async () => {
    if (!user?.id || !gateAgreed) return
    setGateBusy(true)
    setGateError('')
    try {
      await upsertCommunityProfile(user.id, {
        displayName: gateName.trim() || user.user_metadata?.full_name || 'Member',
        shortIntro: gateIntro.trim(),
      })
      await acceptCommunityPromise(user.id)
      await loadMember()
    } catch (err: any) {
      setGateError(err?.message || 'Something went wrong. Please try again.')
    } finally {
      setGateBusy(false)
    }
  }

  const handlePost = async () => {
    if (!user?.id || tab === 'members') return
    if (!draft.trim() && !file) return
    setPosting(true)
    setPostError('')
    try {
      let mediaPath: string | undefined
      let mediaType: 'photo' | 'video' | undefined
      if (file) {
        mediaPath = await uploadCommunityMedia(user.id, file)
        mediaType = file.type.startsWith('video/') ? 'video' : 'photo'
      }
      await createCommunityPost(token, { area: tab, content: draft.trim() || undefined, mediaPath, mediaType })
      setDraft('')
      setFile(null)
      await loadPosts(tab)
    } catch (err: any) {
      setPostError(err?.message || 'Could not share your post right now.')
    } finally {
      setPosting(false)
    }
  }

  if (checkingGate) {
    return (
      <div className="flex justify-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold" />
      </div>
    )
  }

  if (!member?.guidelinesAcknowledgedAt) {
    return (
      <PanelShell
        eyebrow="Fit by Cassie Community"
        title="Welcome to the Community Center"
        subtitle="A private space for active clients to share wins, ask questions, and support each other."
        actions={
          <GradientButton onClick={() => navigate('/dashboard')} variant="outline" size="sm" icon={ArrowLeft}>
            Back to dashboard
          </GradientButton>
        }
      >
        <GlassCard hover={false}>
          <h2 className="font-display text-xl font-semibold text-ink mb-4">Set up your profile</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Display name</label>
              <input
                value={gateName}
                onChange={(e) => setGateName(e.target.value)}
                className="form-input w-full"
                placeholder="How should other members see your name?"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Short intro (optional)</label>
              <input
                value={gateIntro}
                onChange={(e) => setGateIntro(e.target.value)}
                className="form-input w-full"
                placeholder="A line about yourself or your goals"
              />
            </div>
          </div>

          <h2 className="font-display text-xl font-semibold text-ink mt-8 mb-3">The Community Promise</h2>
          <div className="bg-cream rounded-xl p-4 text-sm text-textPrimary whitespace-pre-wrap">
            {COMMUNITY_PROMISE_TEXT}
          </div>
          <label className="flex items-start gap-2 mt-4 text-sm text-textPrimary">
            <input
              type="checkbox"
              checked={gateAgreed}
              onChange={(e) => setGateAgreed(e.target.checked)}
              className="mt-1"
            />
            I've read and agree to the Community Promise.
          </label>
          {gateError && <p className="text-sm text-red-600 mt-3">{gateError}</p>}
          <GradientButton
            onClick={handleAcceptPromise}
            disabled={!gateAgreed || !gateName.trim() || gateBusy}
            className="mt-5 !bg-none !bg-brand-blush !text-brand-navy hover:!bg-none hover:!bg-brand-blush/90 hover:!text-brand-navy"
          >
            {gateBusy ? 'Joining...' : 'Join the Community Center'}
          </GradientButton>
        </GlassCard>
      </PanelShell>
    )
  }

  return (
    <PanelShell
      eyebrow="Fit by Cassie Community"
      title="Community Center"
      subtitle="Announcements, conversations, and wins — just for active Fit by Cassie clients."
      actions={
        <GradientButton onClick={() => navigate('/dashboard')} variant="outline" size="sm" icon={ArrowLeft}>
          Back to dashboard
        </GradientButton>
      }
    >
      <div className="flex flex-wrap gap-2 mb-6">
        {AREAS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              tab === key ? 'bg-ink text-white' : 'bg-white text-gray-500 border border-gray-200 hover:border-gray-300'
            }`}
          >
            <Icon size={15} />
            {label}
          </button>
        ))}
        <button
          onClick={() => setTab('members')}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
            tab === 'members' ? 'bg-ink text-white' : 'bg-white text-gray-500 border border-gray-200 hover:border-gray-300'
          }`}
        >
          <Users size={15} />
          Members
        </button>
      </div>

      {tab === 'members' ? (
        <CommunityMembersPanel userId={user!.id} token={token} />
      ) : (
        <div className="space-y-5">
          <GlassCard hover={false}>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={
                tab === 'announcement' ? 'Share an announcement...' : tab === 'win' ? 'Share a win...' : "What's on your mind?"
              }
              rows={3}
              className="form-input w-full resize-none"
            />
            <div className="flex items-center justify-between mt-3">
              <label className="flex items-center gap-2 text-sm text-gray-500 cursor-pointer hover:text-ink">
                {file?.type.startsWith('video/') ? <Video size={16} /> : <ImageIcon size={16} />}
                {file ? file.name : 'Add a photo or video'}
                <input
                  type="file"
                  accept="image/*,video/*"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </label>
              <GradientButton onClick={handlePost} size="sm" disabled={posting || (!draft.trim() && !file)}>
                {posting ? 'Posting...' : 'Post'}
              </GradientButton>
            </div>
            {postError && <p className="text-sm text-red-600 mt-2">{postError}</p>}
          </GlassCard>

          {loadingPosts ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold" />
            </div>
          ) : posts.length === 0 ? (
            <GlassCard hover={false}>
              <p className="text-center text-gray-500 py-8">Nothing here yet — be the first to share.</p>
            </GlassCard>
          ) : (
            posts.map((post) => (
              <CommunityPostCard
                key={post.id}
                post={post}
                userId={user!.id}
                token={token}
                isAdmin={isAdmin}
                onChanged={() => loadPosts(tab as PostArea)}
              />
            ))
          )}
        </div>
      )}
    </PanelShell>
  )
}

export default CommunityCenter
